import React, { FC, useContext, useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import {
  AppBar,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { getPeriod, scrollToTop } from '../helpers/helpers'
import { DesktopDatePicker } from '@mui/lab'
import { styled } from '@mui/material/styles'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { DataContext } from '../contexts/DataProvider'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
interface RangeSelectProps {
  pdfContainerRef: HTMLDivElement | null
}

const StyledWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  '> div, > button': {
    flexShrink: 0,
  },
}))

const StyledDatePickerWrapper = styled('div')(() => ({
  display: 'flex',
  '> div': {
    maxWidth: '150px',
    margin: '0 0.625rem',
  },
}))

const RangeSelect: FC<RangeSelectProps> = ({ pdfContainerRef }) => {
  const { period, startDate, endDate, setPeriod, setStartDate, setEndDate, setError } = useContext(DataContext)
  const [startDateBeforeSubmit, setStartDateBeforeSubmit] = useState<Dayjs | null>(startDate)
  const [endDateBeforeSubmit, setEndDateBeforeSubmit] = useState<Dayjs | null>(endDate)
  const [errorDateMessage, setErrorDateMessage] = useState('')
  const [exportLoading, setExportLoading] = useState(false)
  const handlePeriod = (event) => {
    setPeriod(event.target.value)
  }

  const isDisabled = () => {
    if (dayjs(endDateBeforeSubmit).add(1, period.toLowerCase()) > dayjs()) {
      return true
    }
    return !!errorDateMessage
  }

  useEffect(() => {
    if (period) {
      setStartDate(dayjs().subtract(1, period))
      setEndDate(dayjs())
      setStartDateBeforeSubmit(dayjs().subtract(1, period))
      setEndDateBeforeSubmit(dayjs())
    }
  }, [period])

  const handleArrows = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setStartDate((prev) => dayjs(prev).subtract(1, period.toLowerCase()))
      setEndDate((prev) => dayjs(prev).subtract(1, period.toLowerCase()))
      setStartDateBeforeSubmit((prev) => dayjs(prev).subtract(1, period.toLowerCase()))
      setEndDateBeforeSubmit((prev) => dayjs(prev).subtract(1, period.toLowerCase()))
    } else if (direction === 'next') {
      if (dayjs(endDateBeforeSubmit).add(1, period.toLowerCase()) < dayjs()) {
        setStartDate((prev) => dayjs(prev).add(1, period.toLowerCase()))
        setEndDate((prev) => dayjs(prev).add(1, period.toLowerCase()))
        setStartDateBeforeSubmit((prev) => dayjs(prev).add(1, period.toLowerCase()))
        setEndDateBeforeSubmit((prev) => dayjs(prev).add(1, period.toLowerCase()))
      } else {
        setErrorDateMessage('End date must be less than today')
      }
    }
  }

  const handleStartDate = (newDate: Dayjs | null) => {
    setStartDateBeforeSubmit(newDate)
  }
  const handleEndDate = (newDate: Dayjs | null) => {
    setEndDateBeforeSubmit(newDate)
  }

  const validateDate = (start, end, period) => {
    if (getPeriod(period) === 'hour' && dayjs(end).diff(start, 'hour', true) > 24) {
      setErrorDateMessage('You can get data from 24 hours at most')
      return false
    }

    if (getPeriod(period) === 'day' && dayjs(end).diff(start, 'day', true) > 31) {
      setErrorDateMessage('You can get data from 31 days at most.')
      return false
    }

    if (getPeriod(period) === 'month' && dayjs(end).diff(start, 'month') > 24) {
      setErrorDateMessage('If you want to get months or weeks, you can get data from 24 months at most.')
      return false
    }

    setErrorDateMessage('')
    return true
  }

  const submitDate = () => {
    setStartDate(dayjs(startDateBeforeSubmit))
    setEndDate(dayjs(endDateBeforeSubmit))
  }
  useEffect(() => {
    validateDate(startDateBeforeSubmit, endDateBeforeSubmit, period)
  }, [startDateBeforeSubmit, endDateBeforeSubmit, period])

  const exportPdf = (value: string) => {
    if (!pdfContainerRef) {
      setError({
        isError: true,
        errorMessage: 'Error during downloading the PDF',
      })
      return
    }

    setExportLoading(true)
    pdfContainerRef.classList.add('no-box-shadow') //remove all boxshadows from elements to avoid bug with html2canvas
    scrollToTop('#main-wrapper')

    html2canvas(pdfContainerRef, {
      width: 1289,
      height: 3692,
      windowHeight: 869,
      windowWidth: 1257,
      scale: 1.0625,
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png')

        const imgWidth = 210 - 10
        const pageHeight = 295
        const imgHeight = (canvas.height * imgWidth) / canvas.width - 10
        let heightLeft = imgHeight
        const doc = new jsPDF('p', 'mm')
        let position = 5

        doc.addImage(imgData, 'PNG', 5, position, imgWidth, imgHeight - 10, 'alias')
        heightLeft -= pageHeight

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight
          doc.addPage()
          doc.addImage(imgData, 'PNG', 5, position, imgWidth, imgHeight, 'alias')
          heightLeft -= pageHeight
        }
        doc.save(`insight-${value.toLowerCase().replace(/ /g, '_')}.pdf`)
      })
      .catch(() =>
        setError({
          isError: true,
          errorMessage: 'Error during downloading the PDF',
        }),
      )
      .finally(() => {
        pdfContainerRef.classList.remove('no-box-shadow')
        setExportLoading(false)
      })
  }

  return (
    <AppBar position="sticky" color="transparent" sx={{ boxShadow: 'none', pl: 3, top: '0px' }}>
      <Paper
        sx={{
          p: 2,
          mt: 2,
          width: '100%',
          minWidth: '400px',
        }}
      >
        {errorDateMessage && (
          <Typography
            variant="caption"
            display="block"
            color="red"
            sx={{ marginBottom: '0.75rem' }}
            data-testid="error-message"
          >
            {errorDateMessage}
          </Typography>
        )}

        <StyledWrapper>
          <IconButton onClick={() => handleArrows('prev')} color="primary" data-testid="date-prev-button">
            <KeyboardArrowLeftIcon />
          </IconButton>
          <StyledDatePickerWrapper>
            {startDateBeforeSubmit && endDateBeforeSubmit && (
              <>
                <DesktopDatePicker
                  label="Start date"
                  id="startDate"
                  inputFormat="DD/MM/YYYY"
                  data-testid="date-start"
                  value={startDateBeforeSubmit}
                  onChange={handleStartDate}
                  maxDate={endDateBeforeSubmit}
                  renderInput={(params) => <TextField {...params} />}
                />
                <DesktopDatePicker
                  label="End date"
                  inputFormat="DD/MM/YYYY"
                  data-testid="date-end"
                  value={endDateBeforeSubmit}
                  onChange={handleEndDate}
                  minDate={startDateBeforeSubmit}
                  maxDate={dayjs()}
                  renderInput={(params) => <TextField {...params} />}
                />
              </>
            )}
          </StyledDatePickerWrapper>
          <IconButton
            onClick={() => handleArrows('next')}
            color="primary"
            disabled={isDisabled()}
            data-testid="date-next-button"
          >
            <KeyboardArrowRightIcon />
          </IconButton>
          <Button
            onClick={submitDate}
            variant={'contained'}
            disabled={!!errorDateMessage}
            data-testid="date-submit-button"
          >
            Submit
          </Button>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={period}
            label="Age"
            onChange={handlePeriod}
            data-testid="date-period-select"
            defaultValue=""
            sx={{ minWidth: '100px', marginLeft: 'auto' }}
          >
            <MenuItem value={'Day'}>Day</MenuItem>
            <MenuItem value={'Week'}>Week</MenuItem>
            <MenuItem value={'Month'}>Month</MenuItem>
            <MenuItem value={'Year'}>Year</MenuItem>
          </Select>
          <IconButton sx={{ marginLeft: 2 }} onClick={() => exportPdf('test')} disabled={exportLoading}>
            {exportLoading ? <CircularProgress size={24} /> : <PictureAsPdfIcon />}
          </IconButton>
        </StyledWrapper>
      </Paper>
    </AppBar>
  )
}
export default RangeSelect
